import { lazy, Suspense, useCallback, useEffect, useMemo, useState } from "react";
import { Activity, ArrowRight, Bot, BrainCircuit, ChevronDown, CircleDot, Compass, FileText, GitBranch, Inbox, KeyRound, Layers3, Orbit, PackageOpen, Radio, ShieldCheck, Sparkles, Tags, Target, Timer } from "lucide-react";
import { api } from "../api";
import { ActivityRow } from "../components/ActivityRow";
import type { TakeoutHousekeepingProps } from "../components/TakeoutHousekeeping";
import { Badge, Button, EmptyState, ErrorState, LoadingState, PageHeader, Panel, PanelHeader } from "../components/UI";
import type { ActivityReceipt, GraphNode, GraphPayload, IntelligenceBriefing, Overview } from "../types";
import type { ActionInboxLane, PendingActionCounts } from "./ActionInboxPage";
import { formatNumber, formatTokens } from "../utils";

const KnowledgeGraph = lazy(async () => ({ default: (await import("../components/KnowledgeGraph")).KnowledgeGraph }));

export function OverviewPage({
  refreshKey,
  onNavigate,
  onOpenActionInbox,
  onOpenNote,
  canEdit,
  pendingActions,
  takeoutHousekeeping,
}: {
  refreshKey: number;
  onNavigate: (page: string, receipt?: ActivityReceipt) => void;
  onOpenActionInbox: (lane?: ActionInboxLane) => void;
  onOpenNote: (id: string) => void;
  canEdit: boolean;
  pendingActions: PendingActionCounts;
  takeoutHousekeeping?: TakeoutHousekeepingProps;
}) {
  const [overview, setOverview] = useState<Overview>();
  const [activity, setActivity] = useState<ActivityReceipt[]>([]);
  const [graph, setGraph] = useState<GraphPayload>({ nodes: [], links: [] });
  const [briefing, setBriefing] = useState<IntelligenceBriefing>();
  const [error, setError] = useState<string>();
  const [loading, setLoading] = useState(true);
  const [deepOpen, setDeepOpen] = useState(false);
  const [deepLoaded, setDeepLoaded] = useState(false);
  const [deepLoading, setDeepLoading] = useState(false);
  const [deepError, setDeepError] = useState<string>();

  const load = useCallback(async () => {
    setLoading(true);
    setError(undefined);
    try {
      const [nextOverview, nextBriefing] = await Promise.all([api.overview(), api.briefing()]);
      setOverview(nextOverview);
      setBriefing(nextBriefing);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Could not load workspace pulse");
    } finally {
      setLoading(false);
    }
  }, []);

  const loadDeep = useCallback(async () => {
    setDeepLoading(true);
    setDeepError(undefined);
    try {
      const [nextActivity, nextGraph] = await Promise.all([api.activity(6), api.graph({ limit: 80 })]);
      setActivity(nextActivity);
      setGraph(nextGraph);
      setDeepLoaded(true);
    } catch (caught) {
      setDeepError(caught instanceof Error ? caught.message : "Could not load workspace insights");
    } finally {
      setDeepLoading(false);
    }
  }, []);

  useEffect(() => {
    setDeepLoaded(false);
    void load();
  }, [load, refreshKey]);

  useEffect(() => {
    if (deepOpen && !deepLoaded && !deepLoading && !deepError) void loadDeep();
  }, [deepError, deepLoaded, deepLoading, deepOpen, loadDeep]);

  const usageMax = useMemo(() => Math.max(1, ...(overview?.usage ?? []).map((point) => point.requests)), [overview]);
  const path = useMemo(() => {
    if (!overview?.usage.length) return "";
    const width = 620;
    const height = 150;
    return overview.usage.map((point, index) => {
      const x = (index / Math.max(1, overview.usage.length - 1)) * width;
      const y = height - (point.requests / usageMax) * (height - 16) - 8;
      return `${index ? "L" : "M"}${x.toFixed(1)},${y.toFixed(1)}`;
    }).join(" ");
  }, [overview, usageMax]);

  if (loading && !overview) return <LoadingState label="Reading the workspace pulse…" />;
  if (error && !overview) return <ErrorState message={error} onRetry={() => void load()} />;
  if (!overview) return null;

  const metricCards = [
    { label: "Memories", value: formatNumber(overview.notes), suffix: "notes", icon: FileText, tone: "cyan" },
    { label: "Links", value: formatNumber(overview.links), suffix: "connections", icon: GitBranch, tone: "violet" },
    { label: "Context packs", value: formatNumber(overview.packs), suffix: "shared packs", icon: PackageOpen, tone: "lime" },
    { label: "Agent requests", value: formatNumber(overview.requests), suffix: `${formatTokens(overview.tokens)} tokens delivered`, icon: Radio, tone: "coral" },
  ];

  return (
    <div className="page overview-page">
      <PageHeader
        eyebrow="Home"
        title="Your knowledge workspace"
        description="Find what you know. Prepare useful context. See what changed."
        actions={<><Button variant="secondary" onClick={() => onNavigate("library")}><Compass size={16} /> Browse knowledge</Button><Button variant="primary" onClick={() => onNavigate("memory")}><Sparkles size={16} /> Prepare task context</Button></>}
      />

      <section className="workspace-start" aria-label="Start a task">
        <button type="button" onClick={() => onNavigate("library")}><FileText size={24} /><span><strong>Find knowledge</strong><small>Search, read and edit your notes.</small></span><ArrowRight size={18} /></button>
        <button type="button" onClick={() => onNavigate("graph")}><GitBranch size={24} /><span><strong>Explore connections</strong><small>Follow links and inspect recent activity on the graph.</small></span><ArrowRight size={18} /></button>
        <button type="button" onClick={() => onNavigate("memory")}><PackageOpen size={24} /><span><strong>Prepare context</strong><small>Tell Lattice the task. Preview the notes to share.</small></span><ArrowRight size={18} /></button>
        <button type="button" onClick={() => onNavigate("activity")}><Activity size={24} /><span><strong>Understand changes</strong><small>See requests and updates in time order.</small></span><ArrowRight size={18} /></button>
      </section>
      {error ? <ErrorState message={error} onRetry={() => void load()} /> : null}

      {canEdit && pendingActions.total ? <Panel className="overview-action-inbox">
        <div className="overview-action-copy"><span className="overview-action-icon"><Inbox size={23} /></span><div><span className="eyebrow">Action required</span><h2>{pendingActions.total} {pendingActions.total === 1 ? "decision needs" : "decisions need"} your review</h2><p>Read each proposed change and its destination before deciding.</p></div></div>
        <div className="overview-action-lanes">
          {pendingActions.proposals ? <button type="button" onClick={() => onOpenActionInbox("proposals")}><Bot size={17} /><span><strong>{pendingActions.proposals} write {pendingActions.proposals === 1 ? "proposal" : "proposals"}</strong><small>Read full agent drafts</small></span><ArrowRight size={15} /></button> : null}
          {pendingActions.enrollments ? <button type="button" onClick={() => onOpenActionInbox("access")}><KeyRound size={17} /><span><strong>{pendingActions.enrollments} access {pendingActions.enrollments === 1 ? "request" : "requests"}</strong><small>Authorize agent identities</small></span><ArrowRight size={15} /></button> : null}
          {pendingActions.packRequests ? <button type="button" onClick={() => onOpenActionInbox("context")}><PackageOpen size={17} /><span><strong>{pendingActions.packRequests} context {pendingActions.packRequests === 1 ? "request" : "requests"}</strong><small>Approve bounded memory</small></span><ArrowRight size={15} /></button> : null}
        </div>
      </Panel> : null}

      <details className="home-upkeep"><summary>Knowledge health & upkeep <span>Optional checks, archive review and task briefing</span></summary>
      {briefing ? <Panel className="overview-active-briefing">
        <div className="overview-briefing-orbit"><Orbit size={22} /></div>
        <div><span className="eyebrow">Task briefing</span><h2>{briefing.headline}</h2><p>{briefing.prompts[0] ?? "No urgent memory housekeeping today."}</p></div>
        <div className="overview-briefing-signals"><span><Target size={14} />{briefing.missions.length} missions</span><span><Radio size={14} />{briefing.changes.total} changes</span></div>
        <Button variant="primary" onClick={() => onNavigate("active")}>Open tasks & upkeep <ArrowRight size={15} /></Button>
      </Panel> : null}

      {overview.orphans || overview.staleSources || overview.proposals ? <Panel className="attention-panel overview-priority-attention">
        <PanelHeader title="Needs attention" eyebrow="Knowledge health" />
        <div className="attention-list">
          <button type="button" onClick={() => onNavigate("library")}><span className="attention-icon amber"><CircleDot size={17} /></span><span><strong>{overview.orphans} notes without links</strong><small>No incoming or outgoing links</small></span><ArrowRight size={15} /></button>
          <button type="button" onClick={() => onNavigate("library")}><span className="attention-icon violet"><Activity size={17} /></span><span><strong>{overview.staleSources} stale sources</strong><small>Provenance may need a refresh</small></span><ArrowRight size={15} /></button>
          {overview.proposals ? <button type="button" onClick={() => onOpenActionInbox("proposals")}><span className="attention-icon cyan"><Bot size={17} /></span><span><strong>{overview.proposals} write proposals</strong><small>Open the dedicated approval queue</small></span><ArrowRight size={15} /></button> : null}
        </div>
      </Panel> : null}

      {canEdit && takeoutHousekeeping ? <Panel className="archive-shortcut"><div><h2>Review imported conversations</h2><p>{takeoutHousekeeping.totalPending} candidates available. Choose what belongs in your knowledge.</p></div><Button variant="secondary" onClick={() => onNavigate("curator")}>Open import review <ArrowRight size={16} /></Button></Panel> : null}
      </details>

      <div className="metric-grid" aria-label="Workspace totals">
        {metricCards.map(({ label, value, suffix, icon: Icon, tone }) => (
          <Panel key={label} className={`metric-card metric-${tone}`}>
            <div className="metric-top"><span>{label}</span><i><Icon size={17} /></i></div>
            <div className="metric-value"><strong>{value}</strong><small>{suffix}</small></div>
            <span className="metric-signal" />
          </Panel>
        ))}
      </div>

      <button
        type="button"
        className="overview-deep-toggle"
        aria-expanded={deepOpen}
        aria-controls="overview-deep-insights"
        onClick={() => {
          setDeepOpen((current) => !current);
          setDeepError(undefined);
        }}
      >
        <span className="overview-deep-icon"><Compass size={20} /></span>
        <span><strong>{deepOpen ? "Hide deeper insights" : "Usage & knowledge details"}</strong><small>Context trends, memory quality, graph, taxonomy and recent receipts</small></span>
        <ChevronDown size={19} aria-hidden="true" />
      </button>

      {deepOpen ? <div id="overview-deep-insights" className="overview-deep-insights">
        {deepError ? <ErrorState message={deepError} onRetry={() => void loadDeep()} /> : !deepLoaded || deepLoading ? <LoadingState label="Mapping deeper workspace signals…" /> : <div className="overview-grid">
        <Panel className="pulse-panel">
          <PanelHeader title="Context delivered" eyebrow="Last 30 days" action={<Button size="sm" variant="ghost" onClick={() => onNavigate("activity")}>Open activity <ArrowRight size={14} /></Button>} />
          {overview.usage.length ? (
            <div className="usage-chart">
              <div className="usage-total"><strong>{formatNumber(overview.requests)}</strong><span>retrievals</span><Badge tone="cyan">{formatTokens(overview.tokens)} tokens</Badge></div>
              <svg viewBox="0 0 620 170" preserveAspectRatio="none" aria-label="Context requests over time">
                <defs><linearGradient id="chartFill" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stopColor="#4de1c1" stopOpacity=".28" /><stop offset="1" stopColor="#4de1c1" stopOpacity="0" /></linearGradient></defs>
                {[30, 70, 110, 150].map((y) => <line key={y} x1="0" x2="620" y1={y} y2={y} className="chart-gridline" />)}
                <path d={`${path} L620,170 L0,170 Z`} fill="url(#chartFill)" />
                <path d={path} className="chart-line" />
              </svg>
              <div className="chart-labels"><span>{overview.usage[0]?.date}</span><span>{overview.usage.at(-1)?.date}</span></div>
            </div>
          ) : (
            <EmptyState title="No context delivered yet" description="Connect an agent and retrieve a Context Pack. The first receipt will appear here." action={<Button size="sm" variant="secondary" onClick={() => onNavigate("agents")}>Connect agent</Button>} />
          )}
        </Panel>

        <Panel className="memory-health-panel">
          <PanelHeader title="Agent memory quality" eyebrow="Measured · last 30 days" action={<BrainCircuit size={18} />} />
          <div className="memory-health-grid">
            <span><ShieldCheck size={17} /><small>Retrieval hit rate</small><strong>{Math.round(overview.memory.hitRate * 100)}%</strong></span>
            <span><Timer size={17} /><small>p95 latency</small><strong>{Math.round(overview.memory.p95LatencyMs)} ms</strong></span>
            <span><Radio size={17} /><small>Knowledge reached</small><strong>{Math.round(overview.memory.retrievalCoverageRate * 100)}%</strong></span>
            <span><GitBranch size={17} /><small>Notes connected</small><strong>{Math.round(overview.memory.linkedNoteRate * 100)}%</strong></span>
            <span><Tags size={17} /><small>Notes classified</small><strong>{Math.round(overview.memory.classifiedNoteRate * 100)}%</strong></span>
            <span><Sparkles size={17} /><small>Smart builds</small><strong>{overview.memory.contextBuilds}</strong></span>
          </div>
          <button type="button" className="memory-health-cta" onClick={() => onNavigate("memory")}><span><strong>Build context for a real task</strong><small>{overview.memory.unusedNoteCount} active notes were not delivered in this 30-day range · {overview.memory.unresolvedLinkCount} unresolved links</small></span><ArrowRight size={15} /></button>
        </Panel>

        <Panel className="overview-graph-panel">
          <PanelHeader title="Knowledge graph" eyebrow={`${graph.nodes.length} visible nodes`} action={<Button size="sm" variant="ghost" onClick={() => onNavigate("graph")}>Explore map <ArrowRight size={14} /></Button>} />
          <div className="overview-graph-wrap">
            <Suspense fallback={<LoadingState label="Loading graph…" />}>
              <KnowledgeGraph data={graph} onSelect={(node: GraphNode) => !["taxonomy", "missing", "agent", "pack"].includes(node.type) && onOpenNote(node.id)} />
            </Suspense>
          </div>
        </Panel>

        <Panel className="taxonomy-panel">
          <PanelHeader title="Taxonomy coverage" eyebrow="Real note distribution" action={<Tags size={17} />} />
          {overview.taxonomy.length ? (
            <div className="taxonomy-bars">
              {overview.taxonomy.slice(0, 7).map((item, index) => {
                const max = Math.max(...overview.taxonomy.map((taxon) => taxon.count), 1);
                return <div key={`${item.kind}:${item.name}`}><span><i className={`taxonomy-color-${index % 4}`} />{item.name}</span><progress max={max} value={item.count} aria-label={`${item.kind} ${item.name}: ${item.count} notes`} /><code>{item.count}</code></div>;
              })}
            </div>
          ) : <EmptyState title="No taxonomy yet" description="Add tags to notes to reveal coverage and agent demand." />}
        </Panel>

        <Panel className="recent-panel">
          <PanelHeader title="Latest receipts" eyebrow="Explainable activity" action={<Button size="sm" variant="ghost" onClick={() => onNavigate("activity")}>View all <ArrowRight size={14} /></Button>} />
          {activity.length ? <div className="activity-list">{activity.map((receipt) => <ActivityRow key={receipt.id} receipt={receipt} onClick={() => receipt.category === "retrieval" ? onNavigate("graph", receipt) : onNavigate("activity")} />)}</div> : <EmptyState title="No agent receipts" description="Every search and Context Pack retrieval will leave an inspectable trail here." />}
        </Panel>

        <Panel className="top-notes-panel">
          <PanelHeader title="Most delivered" eyebrow="Not model usage" action={<Layers3 size={17} />} />
          {overview.mostUsed.length ? <div className="ranked-list">{overview.mostUsed.slice(0, 5).map((note, index) => <button type="button" key={note.id} onClick={() => onOpenNote(note.id)}><i>{String(index + 1).padStart(2, "0")}</i><span><strong>{note.title}</strong><small>{note.type ?? "note"}</small></span><Badge tone="cyan">{note.count} delivered</Badge></button>)}</div> : <EmptyState title="No delivered notes" description="Retrieval counts appear after agents start requesting context." />}
        </Panel>
      </div>}
      </div> : null}
    </div>
  );
}
